import React, { useContext, useEffect, useMemo, useState } from 'react';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';

import Select from 'react-select';
import {
  PageContent,
  PageSection,
  PageHeader,
  ControlContainer,
  ControlContainerRow,
  Subtitle,
  StyledCard,
} from 'common/styles/page';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';

import OutcomesLineChart from './OutcomesLineChart';
import OutcomesBarChart from './OutcomesBarChart';
import ToggleControl from '../../../common/components/ToggleChartControl';
import CategorySelect from 'common/components/CategorySelect';

import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import { useSelector } from 'react-redux';
import { AggregateType } from 'common/constants/AggregateType';
import {
  getSelectedAssessmentPeriodId,
  getCurrentOrganizationId,
  getAssessmentPeriods,
  getSelectedAssessmentPeriodCategories,
} from 'common/selectors/auth';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const OutcomesGroup = () => {
  type SelectOption = { value: string; label: string; priority?: string };
  const [filterCategories, setFilterCategories] = useState<SelectOption[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [selectedOptionName, setSelectedOptionName] = useState<string | null>(null);

  const categorySelectCategories = useMemo(
    () =>
      filterCategories.map(option => ({
        id: option.value,
        name: option.label,
        priority: option.priority,
      })),
    [filterCategories]
  );

  const [filterOutcomes, setFilterOutcomes] = useState<SelectOption[]>([]);
  const hasOutcomes = filterOutcomes.length > 0;
  const shouldShowNoDataMessage = !hasOutcomes;
  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(null);
  const [selectedOutcomeName, setSelectedOutcomeName] = useState<string | null>(null);

  const [numAssessmentPeriods, setNumAssessmentPeriods] = useState(0);

  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const organizationId = useSelector(getCurrentOrganizationId);
  const assessmentPeriods = useSelector(getAssessmentPeriods);
  const selectedAssessmentPeriodCategories = useSelector(getSelectedAssessmentPeriodCategories) as any[];
  const { setTopBarContent } = useContext(TopBarContext);
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  useEffect(() => {
    setTopBarContent(<TopBarControls />);
    return () => setTopBarContent(null);
  }, [setTopBarContent]);

  const { data: outcomes_data } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.OPTION_OUTCOME_SCORES,
    organizationId,
  });

  const [lineChartData, setLineChartData] = useState<Array<any>>([]);
  const [chartType, setChartType] = useState('bar');

  const contextAssessmentPeriod =
    (Array.isArray(assessmentPeriods)
      ? assessmentPeriods.find((period: any) => period?.id?.toString?.() === assessmentPeriodId?.toString?.())
      : undefined) || assessmentPeriods?.[0];

  const normalizeOutcomeOptions = (outcomes: any): SelectOption[] => {
    if (!Array.isArray(outcomes)) return [];
    const outcomeMap = new Map<string, SelectOption>();

    outcomes.forEach((outcome: any) => {
      const label = outcome?.name || '';
      const value = outcome?.id?.toString?.() || '';
      if (value && label) {
        outcomeMap.set(value, { value, label });
      }
    });

    return Array.from(outcomeMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  };

  const availableOutcomes = useMemo(
    () => normalizeOutcomeOptions((contextAssessmentPeriod as any)?.outcomes || []),
    [contextAssessmentPeriod]
  );

  useEffect(() => {
    const categories: SelectOption[] = Array.isArray(selectedAssessmentPeriodCategories)
      ? selectedAssessmentPeriodCategories
          .map((category: any) => ({
            value: category?.id?.toString?.() || category?.name || '',
            label: category?.name || category?.label || category?.category_name || category?.title || '',
            priority: category?.priority,
          }))
          .filter(category => category.value && category.label)
      : [];

    setFilterCategories(categories);

    if (categories.length > 0) {
      const primaryCategory = categories.find(option => option.priority === 'primary');
      const defaultCategory = primaryCategory ?? categories[0];
      setSelectedCategory(defaultCategory?.value ?? null);
      setSelectedCategoryName(defaultCategory?.label ?? null);
    }
  }, [selectedAssessmentPeriodCategories]);

  useEffect(() => {
    setFilterOutcomes(availableOutcomes);

    if (availableOutcomes.length === 0) {
      if (selectedOutcome !== null) {
        setSelectedOutcome(null);
      }
      if (selectedOutcomeName !== null) {
        setSelectedOutcomeName(null);
      }
      return;
    }

    const selectedMatch = availableOutcomes.find(option => option.value === selectedOutcome);
    if (selectedMatch) {
      if (selectedOutcomeName !== selectedMatch.label) {
        setSelectedOutcomeName(selectedMatch.label);
      }
      return;
    }

    setSelectedOutcome(availableOutcomes[0].value);
    setSelectedOutcomeName(availableOutcomes[0].label);
  }, [availableOutcomes, selectedOutcome, selectedOutcomeName]);

  useEffect(() => {
    const outcomeLabel = selectedOutcomeName || 'the selected outcome';
    const categoryLabel = selectedCategoryName || 'the selected category';
    const optionLabel = selectedOptionName ? ` for ${selectedOptionName}` : '';

    setPagePrompts([
      {
        title: 'Group Outcomes Overview',
        displayText: `Summarize group outcomes for ${outcomeLabel} by ${categoryLabel}.${optionLabel}`,
        prompt:
          `Review the Group Outcomes page for ${outcomeLabel} by ${categoryLabel}${optionLabel} and summarize the key signals from the outcomes data. Identify which teams or categories are performing best and which need attention, then explain what this means for leadership decisions.`,
      },
      {
        title: 'Group Outcome Gaps',
        displayText: `Find the biggest gaps for ${outcomeLabel} by ${categoryLabel}.${optionLabel}`,
        prompt:
          `Identify the biggest gaps and risks in the group outcomes data shown on this page for ${outcomeLabel} by ${categoryLabel}${optionLabel}. Explain which teams or categories are lagging, what may be contributing to that gap, and where leaders should focus first.`,
      },
      {
        title: 'Group Outcome Actions',
        displayText: `Recommend actions for ${outcomeLabel} by ${categoryLabel}.${optionLabel}`,
        prompt:
          `Based on the group outcomes trends for ${outcomeLabel} by ${categoryLabel}${optionLabel}, recommend practical next steps leadership should take. Focus on actions that can improve group performance, align outcomes with culture, and support the groups that need it most.`,
      },
    ]);

    setPageInfoTitle('About Group Outcomes');
    setPageInfoIntro(
      'This page shows how team-level outcomes are shifting over time and across categories. It helps leaders spot performance variation and select areas where team-focused action can improve results.',
    );
    setPageInfoItems([
      {
        term: 'Team performance',
        description: 'See how teams and categories compare on key outcome metrics.',
      },
      {
        term: 'Trend focus',
        description: 'Use the trend data to prioritize where to invest team improvement efforts.',
      },
      {
        term: 'Leadership actions',
        description: 'Turn team outcome observations into practical action steps.',
      },
    ]);

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro, selectedOutcomeName, selectedCategoryName, selectedOptionName]);

  useEffect(() => {
    if (outcomes_data && outcomes_data.length > 0) {
      const accumulatedData: any[] = [];
      let dataSetCounter = 0;

      const getPayloadPeriodId = (item: any) => item?.result?.assessment_period_id ?? item?.assessment_period;
      const getStorePeriod = (item: any) =>
        assessmentPeriods?.find(
          (period: any) => period?.id?.toString?.() === getPayloadPeriodId(item)?.toString?.()
        );
      const getPeriodStart = (item: any) =>
        item?.result?.assessment_period_start ?? item?.assessment_period?.toString?.() ?? '';
      const getPeriodStartTime = (item: any) => {
        const storePeriod = getStorePeriod(item);
        if (storePeriod?.startDateTime) {
          const parsed = Date.parse(storePeriod.startDateTime);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }

        const start = getPeriodStart(item);
        const timestamp = Date.parse(start);
        if (!Number.isNaN(timestamp)) {
          return timestamp;
        }

        return Number(getPayloadPeriodId(item)) || 0;
      };
      const getPeriodLabel = (item: any) => {
        const storePeriod = getStorePeriod(item);
        if (storePeriod?.name) {
          return storePeriod.name;
        }

        const payloadPeriodName = item?.result?.assessment_period_name;
        if (payloadPeriodName) {
          return payloadPeriodName;
        }

        return item?.assessment_period?.toString?.() ?? `Period ${getPayloadPeriodId(item) ?? dataSetCounter}`;
      };
      const getOptionName = (entry: any) => entry?.option_name ?? entry?.optionName ?? '';
      const getCategoryId = (entry: any) =>
        entry?.category_id?.toString?.() ?? entry?.categoryId?.toString?.() ?? '';

      const sortedAllOutcomesData = [...outcomes_data].sort(
        (a, b) => getPeriodStartTime(a) - getPeriodStartTime(b)
      );

      const computeOptionScores = (outcome: any) => {
        const allAggregates = (outcome?.questions ?? []).flatMap((question: any) => question?.aggregates ?? []);

        const filtered = allAggregates.filter((aggregate: any) => {
          const categoryId = getCategoryId(aggregate);
          if (selectedCategory && selectedCategory !== '') {
            return (
              categoryId === selectedCategory ||
              aggregate?.category_name === selectedCategory ||
              aggregate?.categoryName === selectedCategory
            );
          }
          return true;
        });

        const groupedByOption = new Map<
          string,
          { option_name: string; overall_score: number; n: number; totalN: number; color: string }
        >();

        filtered.forEach((aggregate: any) => {
          const optionKey =
            aggregate?.option_id?.toString?.() ||
            aggregate?.optionId?.toString?.() ||
            aggregate?.option_name ||
            aggregate?.optionName ||
            '';
          const optionName = getOptionName(aggregate) || optionKey || 'Unknown';
          const score = Number(aggregate?.overall_score ?? aggregate?.average_response ?? 0);
          const count = Number(aggregate?.n ?? 0) || 0;
          const color = aggregate?.color || '#4a90e2';

          if (!optionKey) {
            return;
          }

          if (!groupedByOption.has(optionKey)) {
            groupedByOption.set(optionKey, {
              option_name: optionName,
              overall_score: score,
              n: count,
              totalN: count,
              color,
            });
            return;
          }

          const existing = groupedByOption.get(optionKey)!;
          if (count > 0) {
            const totalScore = existing.overall_score * existing.totalN + score * count;
            existing.totalN += count;
            existing.overall_score = existing.totalN ? totalScore / existing.totalN : existing.overall_score;
            existing.n = Math.max(existing.n, count);
          } else {
            existing.overall_score = (existing.overall_score + score) / 2;
          }
          existing.color = existing.color || color;
        });

        return Array.from(groupedByOption.values()).sort((a, b) => a.option_name.localeCompare(b.option_name));
      };

      sortedAllOutcomesData.forEach(item => {
        const result = item?.result;
        const outcomesArray = Array.isArray(result)
          ? result
          : Array.isArray(result?.outcomes)
          ? result.outcomes
          : [];

        const outcome = outcomesArray.find(
          (outcome: any) =>
            outcome?.outcome_id?.toString?.() === selectedOutcome || outcome?.outcome_name === selectedOutcome
        );

        const sortedCategoryData = outcome ? computeOptionScores(outcome) : [];
        const storePeriod = getStorePeriod(item);
        const startDateValue = storePeriod?.startDateTime || item?.result?.assessment_period_start || '';

        accumulatedData.push({
          data_set: dataSetCounter++,
          data: sortedCategoryData,
          name: getPeriodLabel(item),
          start_date: startDateValue,
          end_date: item?.result?.assessment_period_end || '',
        });
      });

      setLineChartData(accumulatedData);
      setNumAssessmentPeriods(outcomes_data.length);
    } else {
      setLineChartData([]);
    }
  }, [outcomes_data, selectedCategory, selectedOption, selectedOutcome]);

  return (
    <PageContent>
      <PageSection padbottom="10px">
        <PageHeader
          title="Group Outcomes"
          controls={
            <ControlContainerRow>
              <ControlContainer justify="left">
                {numAssessmentPeriods > 1 && (
                  <ToggleControl
                    buttonLabels={[
                      { type: 'bar', icon: 'chart bar' },
                      { type: 'line', icon: 'line graph', disabled: numAssessmentPeriods < 2 },
                    ]}
                    setChartType={setChartType}
                  />
                )}
              </ControlContainer>
            </ControlContainerRow>
          }
          caption="Outcome sentiment by assessment period"
        />
      </PageSection>
      <PageSection display="flex" gap="10px" alignItems="flex-start" style={{ minWidth: '400px' }}>
        <StyledCard>
          <PageSection padtop padright="15px" padleft padbottom>
            {!shouldShowNoDataMessage && (
              <PageSection padbottom display="flex" justifyContent="space-between">
                <Subtitle>
                  {selectedOptionName
                    ? `${selectedOutcomeName} for ${selectedOptionName}`
                    : `${selectedOutcomeName} by ${selectedCategoryName}`}
                </Subtitle>

                <ControlContainer justify="right">
                  <Select
                    options={filterOutcomes}
                    placeholder="Select Outcome"
                    value={filterOutcomes.find(option => option.value === selectedOutcome)}
                    onChange={option => {
                      setSelectedOutcome(option ? option.value : null);
                      setSelectedOutcomeName(option ? option.label : null);
                    }}
                    styles={{
                      container: base => ({
                        ...base,
                        width: '250px',
                      }),
                    }}
                  />
                  <CategorySelect
                    categories={categorySelectCategories}
                    selectedCategory={selectedCategory}
                    onCategoryChange={categoryId => {
                      setSelectedCategory(categoryId);
                      const selectedOption = filterCategories.find(option => option.value === categoryId);
                      setSelectedCategoryName(selectedOption ? selectedOption.label : null);
                      setSelectedOption(null);
                      setSelectedOptionName(null);
                    }}
                    placeholder="Select Category"
                    width="250px"
                  />
                </ControlContainer>
              </PageSection>
            )}
            <PageSection display="flex" justifyContent="center" style={{ height: '490px' }}>
              {shouldShowNoDataMessage ? (
                <PageSection display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 300, width: '100%' }}>
                  <div>No outcomes data is available for the selected assessment period.</div>
                </PageSection>
              ) : chartType === 'line' ? (
                <OutcomesLineChart lineChartData={lineChartData} />
              ) : (
                <OutcomesBarChart lineChartData={lineChartData} />
              )}
            </PageSection>
          </PageSection>
        </StyledCard>
      </PageSection>
    </PageContent>
  );
};

export default OutcomesGroup;


